/**
 * @copyright Copyright 2021 Epic Systems Corporation
 * @file A badge for an app highlighting a feature
 * 		Currently used by widgets but could potentially be used elsewhere
 * @module Epic.AppOrchard.Dashboard.WidgetAppBadge
 */

import { Box, ButtonProps, Center } from "@chakra-ui/react";
import { AOTooltip } from "ao/components/Core";
import React, { FC, memo } from "react";

interface IProps {
	title: string;
	value?: string;
	children: React.ReactNode;
	useHoverTooltip?: boolean;
	asButton?: boolean;
	buttonProps?: ButtonProps;
}

/** A badge for an app highlighting a feature
 * 		Currently used by widgets but could potentially be used elsewhere */
export const WidgetAppBadge: FC<IProps> = memo((props: IProps) => {
	const { title, value, useHoverTooltip, children, asButton, buttonProps } = props;
	const height = "40px";
	const minWidth = "40px";
	const content = (
		<Center
			h={height}
			minW={minWidth}
			display="inline-block"
			bg="#eeebeb"
			p="5px"
			color="#6d6767"
			borderRadius="2px"
			title={!useHoverTooltip ? title : ""}
		>
			{value && (
				<Box fontSize="1.1em" fontWeight="bold" as="span">
					{value}
				</Box>
			)}
			{children}
		</Center>
	);
	return useHoverTooltip ? (
		<AOTooltip
			helptext={title}
			asButton={asButton}
			buttonProps={{ ...buttonProps, h: height, minW: minWidth }}
		>
			{content}
		</AOTooltip>
	) : (
		<>{content}</>
	);
});
